import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  SalesOpportunity,
  SalesOpportunityPricingLineUpsert,
  SalesOpportunityTeamMember,
  SalesOpportunityUpsertPayload,
} from '../../services/salesOpportunitiesApi';
import { Company, listCompanies } from '../../services/coreCompaniesApi';
import { MarketSegment } from '../../services/marketSegmentsApi';
import { listSalesProducts, SalesProductSummary } from '../../services/salesProductsApi';
import { usePermissions } from '../../contexts/PermissionsContext';
import { OPPORTUNITY_STAGES } from '../../types/OpportunityStage';
import { CompanyQuickCreateModal } from './CompanyQuickCreateModal';
import { ModalPortalShell } from '../ui/ModalPortalShell';
import { RequiredMark } from '../ui/RequiredMark';

type OpportunityFormModalProps = {
  open: boolean;
  opportunity?: SalesOpportunity | null;
  teamMembers: SalesOpportunityTeamMember[];
  marketSegments: MarketSegment[];
  onClose: () => void;
  onSubmit: (payload: SalesOpportunityUpsertPayload) => Promise<void>;
};

const PRICING_NATURES = [
  { id: 'Setup', label: 'Setup' },
  { id: 'Development', label: 'Desenvolvimento' },
  { id: 'Recurring', label: 'Recorrente' },
] as const;

const ENGAGEMENT_STATUSES = [
  { id: 'Active', label: 'Ativa' },
  { id: 'AwaitingClient', label: 'Sem contato / aguardando cliente' },
  { id: 'Paused', label: 'Pausada' },
] as const;

const emptyPricingLine = (): SalesOpportunityPricingLineUpsert => ({
  nature: 'Setup',
  amount: 0,
  sortOrder: 0,
});

const emptyPayload: SalesOpportunityUpsertPayload = {
  title: '',
  description: '',
  clientName: '',
  amount: 0,
  monthlyRecurringAmount: 0,
  revenueType: 'Recurring',
  stage: 'Lead',
  engagementStatus: 'Active',
  probabilityPercent: 10,
  pricingLines: [],
};

function sumAmounts(lines: SalesOpportunityPricingLineUpsert[]): {
  amount: number;
  monthlyRecurringAmount: number;
} {
  const amount = lines
    .filter((line) => line.nature === 'Setup' || line.nature === 'Development')
    .reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const monthlyRecurringAmount = lines
    .filter((line) => line.nature === 'Recurring')
    .reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  return { amount, monthlyRecurringAmount };
}

export function OpportunityFormModal({
  open,
  opportunity,
  teamMembers,
  marketSegments,
  onClose,
  onSubmit,
}: OpportunityFormModalProps) {
  const { canWriteModule, canReadModule } = usePermissions();
  const canCreateCompany = canWriteModule('core.companies');
  const canManageProducts = canReadModule('sales.products');
  const [form, setForm] = useState<SalesOpportunityUpsertPayload>(emptyPayload);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [products, setProducts] = useState<SalesProductSummary[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  useEffect(() => {
    if (!open) return;

    async function loadAuxiliaryData(): Promise<void> {
      setLoadingCompanies(true);
      try {
        const companyResult = await listCompanies({ activeOnly: true, page: 1, pageSize: 200 });
        setCompanies(companyResult.items);

        if (canManageProducts) {
          const productResult = await listSalesProducts({ activeOnly: true, page: 1, pageSize: 200 });
          setProducts(productResult.items);
        } else {
          setProducts([]);
        }
      } catch {
        toast.error('Falha ao carregar dados auxiliares.');
      } finally {
        setLoadingCompanies(false);
      }
    }

    void loadAuxiliaryData();

    if (opportunity) {
      const pricingLines =
        opportunity.pricingLines?.map((line, index) => ({
          nature: line.nature,
          amount: line.amount,
          salesProductId: line.salesProductId ?? undefined,
          notes: line.notes ?? undefined,
          sortOrder: line.sortOrder ?? index,
        })) ?? [];
      const totals = pricingLines.length > 0 ? sumAmounts(pricingLines) : null;
      setForm({
        title: opportunity.title,
        description: opportunity.description ?? '',
        clientName: opportunity.clientName ?? '',
        companyId: opportunity.companyId ?? undefined,
        marketSegmentId: opportunity.marketSegmentId ?? undefined,
        projectName: opportunity.projectName ?? '',
        salesProductId: opportunity.salesProductId ?? undefined,
        prospectId: opportunity.prospectId ?? undefined,
        ownerUserId: opportunity.ownerUserId ?? undefined,
        amount: totals?.amount ?? opportunity.amount,
        monthlyRecurringAmount: totals?.monthlyRecurringAmount ?? opportunity.monthlyRecurringAmount,
        revenueType: opportunity.revenueType,
        stage: opportunity.stage,
        engagementStatus: opportunity.engagementStatus ?? 'Active',
        probabilityPercent: opportunity.probabilityPercent,
        expectedCloseDateUtc: opportunity.expectedCloseDateUtc ?? undefined,
        nextContactAtUtc: opportunity.nextContactAtUtc ?? undefined,
        pricingLines,
      });
      setSelectedProductIds(
        opportunity.salesProductId ? new Set([opportunity.salesProductId]) : new Set(),
      );
    } else {
      setForm(emptyPayload);
      setSelectedProductIds(new Set());
    }
  }, [open, opportunity, canManageProducts]);

  if (!open) return null;

  function toggleProduct(productId: string): void {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }

  function updatePricingLine(
    index: number,
    patch: Partial<SalesOpportunityPricingLineUpsert>,
  ): void {
    const pricingLines = [...(form.pricingLines ?? [])];
    pricingLines[index] = { ...pricingLines[index], ...patch };
    const totals = sumAmounts(pricingLines);
    setForm({ ...form, pricingLines, ...totals });
  }

  function addPricingLine(): void {
    const pricingLines = [...(form.pricingLines ?? []), emptyPricingLine()];
    setForm({ ...form, pricingLines });
  }

  function removePricingLine(index: number): void {
    const pricingLines = (form.pricingLines ?? []).filter((_, i) => i !== index);
    const totals = sumAmounts(pricingLines);
    setForm({ ...form, pricingLines, ...totals });
  }

  function handleCompanyCreated(company: Company): void {
    setCompanies((prev) =>
      [...prev, company].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    );
    setForm((prev) => ({
      ...prev,
      companyId: company.id,
      clientName: company.name,
    }));
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (form.engagementStatus === 'Paused' && !form.nextContactAtUtc) {
      toast.error('Informe o próximo contato para oportunidades pausadas.');
      return;
    }

    setSaving(true);
    try {
      const pricingLines = (form.pricingLines ?? []).map((line, index) => ({
        ...line,
        sortOrder: index,
      }));
      const totals = pricingLines.length > 0 ? sumAmounts(pricingLines) : {
        amount: form.amount,
        monthlyRecurringAmount: form.monthlyRecurringAmount,
      };
      const [primaryProductId] = Array.from(selectedProductIds);
      await onSubmit({
        ...form,
        ...totals,
        pricingLines,
        companyId: form.companyId || undefined,
        clientName: form.clientName || undefined,
        salesProductId: primaryProductId,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const hasPricingLines = (form.pricingLines?.length ?? 0) > 0;

  return (
    <>
      <ModalPortalShell open={open} panelClassName="erp-card max-w-2xl shadow-xl" overlayClassName="z-[50]">
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="shrink-0 border-b border-secondary-200 px-5 py-4">
              <h2 className="erp-card-title">
                {opportunity ? 'Editar oportunidade' : 'Nova oportunidade'}
              </h2>
            </div>

            <div className="erp-card-body erp-form min-h-0 flex-1 overflow-y-auto">
            <label className="erp-label">
              Título <RequiredMark />
              <input
                className="erp-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </label>

            <div className="erp-form-grid">
              <label className="erp-label">
                Empresa
                <div className="flex gap-2">
                  <select
                    className="erp-input flex-1"
                    value={form.companyId ?? ''}
                    disabled={loadingCompanies}
                    onChange={(e) => {
                      const companyId = e.target.value || undefined;
                      const selected = companies.find((item) => item.id === companyId);
                      setForm({
                        ...form,
                        companyId,
                        clientName: selected?.name ?? form.clientName,
                      });
                    }}
                  >
                    <option value="">Sem empresa vinculada</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  {canCreateCompany ? (
                    <button
                      type="button"
                      className="erp-btn-secondary shrink-0"
                      onClick={() => setCompanyModalOpen(true)}
                    >
                      Nova
                    </button>
                  ) : null}
                </div>
              </label>
              <label className="erp-label">
                Nome (legado / fallback)
                <input
                  className="erp-input"
                  value={form.clientName ?? ''}
                  onChange={(e) => setForm({ ...form, clientName: e.target.value })}
                  placeholder="Usado se não houver empresa"
                />
              </label>
              <label className="erp-label">
                Segmento de mercado
                <select
                  className="erp-input"
                  value={form.marketSegmentId ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      marketSegmentId: e.target.value || undefined,
                    })
                  }
                >
                  <option value="">Nenhum</option>
                  {marketSegments.map((segment) => (
                    <option key={segment.id} value={segment.id}>
                      {segment.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Status de acompanhamento
                <select
                  className="erp-input"
                  value={form.engagementStatus ?? 'Active'}
                  onChange={(e) => setForm({ ...form, engagementStatus: e.target.value })}
                >
                  {ENGAGEMENT_STATUSES.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Próximo contato
                <input
                  type="date"
                  className="erp-input"
                  value={form.nextContactAtUtc?.slice(0, 10) ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      nextContactAtUtc: e.target.value
                        ? new Date(`${e.target.value}T12:00:00`).toISOString()
                        : undefined,
                    })
                  }
                  required={form.engagementStatus === 'Paused'}
                />
              </label>
              <label className="erp-label">
                Estágio
                <select
                  className="erp-input"
                  value={form.stage}
                  onChange={(e) => setForm({ ...form, stage: e.target.value })}
                >
                  {OPPORTUNITY_STAGES.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Probabilidade (%)
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="erp-input"
                  value={form.probabilityPercent}
                  onChange={(e) => setForm({ ...form, probabilityPercent: Number(e.target.value) })}
                />
              </label>
              <label className="erp-label">
                Responsável
                <select
                  className="erp-input"
                  value={form.ownerUserId ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, ownerUserId: e.target.value || undefined })
                  }
                >
                  <option value="">Eu (padrão)</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Previsão de fechamento
                <input
                  type="date"
                  className="erp-input"
                  value={form.expectedCloseDateUtc?.slice(0, 10) ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      expectedCloseDateUtc: e.target.value
                        ? new Date(`${e.target.value}T12:00:00`).toISOString()
                        : undefined,
                    })
                  }
                />
              </label>
            </div>

            {canManageProducts ? (
              <div>
                <p className="text-sm font-medium text-secondary-800">Produtos vinculados</p>
                <p className="erp-hint mb-2">
                  Selecione os produtos comerciais associados a este lead. O primeiro marcado será o
                  produto principal da oportunidade.
                </p>
                {products.length === 0 ? (
                  <p className="text-sm text-secondary-500">Nenhum produto ativo cadastrado.</p>
                ) : (
                  <div className="erp-form-grid">
                    {products.map((product) => (
                      <label
                        key={product.id}
                        className="flex items-center gap-2 rounded-lg border border-secondary-200 px-3 py-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={selectedProductIds.has(product.id)}
                          onChange={() => toggleProduct(product.id)}
                        />
                        {product.logoBase64 ? (
                          <img src={product.logoBase64} alt="" className="h-6 w-6 object-contain" />
                        ) : null}
                        <span>{product.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            <div className="space-y-2 rounded-lg border border-secondary-200 p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-secondary-900">Precificação</p>
                  <p className="text-xs text-secondary-500">
                    Setup e Desenvolvimento somam o valor; Recorrente soma o MRR.
                  </p>
                </div>
                <button type="button" className="erp-btn-secondary" onClick={addPricingLine}>
                  Adicionar linha
                </button>
              </div>

              {(form.pricingLines ?? []).map((line, index) => (
                <div key={`pricing-${index}`} className="erp-form-grid items-end">
                  <label className="erp-label">
                    Natureza
                    <select
                      className="erp-input"
                      value={line.nature}
                      onChange={(e) => updatePricingLine(index, { nature: e.target.value })}
                    >
                      {PRICING_NATURES.map((nature) => (
                        <option key={nature.id} value={nature.id}>
                          {nature.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="erp-label">
                    Valor (R$)
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      className="erp-input"
                      value={line.amount}
                      onChange={(e) => updatePricingLine(index, { amount: Number(e.target.value) })}
                    />
                  </label>
                  <label className="erp-label">
                    Observação
                    <input
                      className="erp-input"
                      value={line.notes ?? ''}
                      onChange={(e) => updatePricingLine(index, { notes: e.target.value })}
                    />
                  </label>
                  <button
                    type="button"
                    className="erp-btn-secondary mb-1"
                    onClick={() => removePricingLine(index)}
                  >
                    Remover
                  </button>
                </div>
              ))}

              <div className="erp-form-grid">
                <label className="erp-label">
                  Valor total (R$)
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    className="erp-input"
                    value={form.amount}
                    disabled={hasPricingLines}
                    onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
                  />
                </label>
                <label className="erp-label">
                  MRR (R$)
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    className="erp-input"
                    value={form.monthlyRecurringAmount}
                    disabled={hasPricingLines}
                    onChange={(e) =>
                      setForm({ ...form, monthlyRecurringAmount: Number(e.target.value) })
                    }
                  />
                </label>
              </div>
            </div>

            <label className="erp-label">
              Descrição
              <textarea
                className="erp-input min-h-[80px]"
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>

            </div>

            <div className="erp-btn-actions shrink-0 border-t border-secondary-200 bg-white px-5 py-4">
              <button type="button" className="erp-btn-secondary" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
      </ModalPortalShell>

      <CompanyQuickCreateModal
        open={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        onCreated={handleCompanyCreated}
      />
    </>
  );
}
